import React from 'react';
import _ from 'underscore';

import isArchived from 'archive/isArchived';
import DataConnector from 'connect/DataConnector';
import * as DataTableConfigActions from 'notebook/data-tables/DataTableConfigActions';
import DataTableConfigStore from 'notebook/data-tables/DataTableConfigStore';
import * as DataTableConstants from 'notebook/data-tables/DataTableConstants';
import DataTableColumnPicker from 'notebook/tables/lookup-field/DataTableColumnPicker';
import {ASSAY_TYPES} from 'results/assay-schemas/AssaySchemaConstants';
import {MODEL_NAMES} from 'results/ResultsNamingConstants';
import FormGroup from 'ui-library/bs/FormGroup';
import Label from 'ui-library/bs/Label';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import {MenuWidth} from 'ui-library/menus/MenuConstants';
import SearchableMenu from 'ui-library/menus/SearchableMenu';

interface EnhancedProps {
  dataTableConfigs: Array<DataTableConstants.FromServerDataTableConfig> | null;
}

class DataTableConfigFieldSelectorConnector extends DataConnector<
  DataTableConfigFieldSelectorProps,
  EnhancedProps
> {
  override shouldSubscribeToRedux = false;
  override stores = [DataTableConfigStore];

  enhanceProps(): EnhancedProps {
    let dataTableConfigs = this.dataTableConfigStore().getDataTableConfigs();
    if (dataTableConfigs != null) {
      dataTableConfigs = dataTableConfigs.filter(
        (dataTableConfig) =>
          dataTableConfig.columns.length > 0 &&
          dataTableConfig.assaySchema.schemaType === ASSAY_TYPES.ASSAY_RESULT &&
          !isArchived(dataTableConfig.assaySchema)
      );
    }
    return {dataTableConfigs};
  }
}

interface DataTableConfigFieldSelectorProps {
  dataTableConfig: DataTableConstants.FromServerDataTableConfig | null;
  dataTableColumn: DataTableConstants.FromServerDataTableColumn | null;
  onDataTableColumnChange: (
    dataTableConfig: DataTableConstants.FromServerDataTableConfig | null,
    dataTableColumn: DataTableConstants.FromServerDataTableColumn | null
  ) => void;
}

class DataTableConfigFieldSelector extends React.Component<
  DataTableConfigFieldSelectorProps & EnhancedProps
> {
  override componentDidMount(): void {
    this._trySelectDefaultConfigAndColumn(this.props);
    DataTableConfigActions.fetchAllDataTableConfigs();
  }

  override UNSAFE_componentWillReceiveProps(
    nextProps: DataTableConfigFieldSelectorProps & EnhancedProps
  ): void {
    this._trySelectDefaultConfigAndColumn(nextProps);
  }

  _trySelectDefaultConfigAndColumn(props: DataTableConfigFieldSelectorProps & EnhancedProps): void {
    const {dataTableConfig, dataTableColumn, dataTableConfigs} = props;

    if (dataTableConfig == null && dataTableColumn == null && dataTableConfigs != null) {
      if (dataTableConfigs.length > 0 && dataTableConfigs[0].columns.length > 0) {
        props.onDataTableColumnChange(dataTableConfigs[0], dataTableConfigs[0].columns[0]);
      }
    }
  }

  _handleDataTableConfigChange = (configId: string): void => {
    const dataTableConfig =
      configId == null ? null : _.findWhere(this.props.dataTableConfigs!, {id: configId})!;

    this.props.onDataTableColumnChange(
      dataTableConfig,
      dataTableConfig == null ? null : dataTableConfig.columns[0]
    );
  };

  override render(): JSX.Element {
    return (
      <div>
        {this.props.dataTableConfigs == null ? (
          <div className="text-center">
            <HorizontalThrobber />
          </div>
        ) : (
          <div>
            <FormGroup>
              <Label className="u-text-uppercase">{MODEL_NAMES.Result_Table} Schema</Label>
              <SelectDropdown
                displayName={this.props.dataTableConfig ? this.props.dataTableConfig.name : 'Select a schema'}
                menu={
                  <SearchableMenu
                    onSelect={(selectedValue) => {
                      this._handleDataTableConfigChange(selectedValue);
                    }}
                    optionDatas={this.props.dataTableConfigs.map((dataTableConfig) => ({
                      value: dataTableConfig.id,
                      name: dataTableConfig.name,
                    }))}
                    selectedValue={this.props.dataTableConfig?.id ?? null}
                    width={MenuWidth.FULL}
                  />
                }
              />
            </FormGroup>
            <DataTableColumnPicker
              allColumns={this.props.dataTableConfig ? this.props.dataTableConfig.columns : []}
              selectedColumnId={this.props.dataTableColumn && this.props.dataTableColumn.id}
              onSelectedColumnChange={(column: DataTableConstants.FromServerDataTableColumn) => {
                this.props.onDataTableColumnChange(this.props.dataTableConfig, column);
              }}
            />
          </div>
        )}
      </div>
    );
  }
}

export {DataTableConfigFieldSelector};
export default DataTableConfigFieldSelectorConnector.connect(DataTableConfigFieldSelector);
